import { v4 as uuidv4 } from "uuid";
import {
  QUALIFYING_COLUMN_FIELDS,
  QUALIFYING_FIELDS_BY_ARRANGEMENT,
} from "./sharedBasedPaymentCalculation";

export const SHARE_BASED_PAYMENT_DATA_SCHEMA_VERSION = 1;

export const createInitialCostPool = (initialValues = {}) => ({
  poolName: "",
  acquiredShareCategory: "companyTreasury",
  issuer: "",
  shareClass: "",
  acquirerCostOwner: "Company",
  method: "fifo",
  openingShares: 0,
  openingCost: 0,
  openingSpecialAccount: 0,
  ...initialValues,
  poolId: initialValues.poolId || "pool-001",
});

export const costMethodOptions = [
  { value: "fifo", label: "FIFO" },
  {
    value: "transactionWeightedAverage",
    label: "Weighted average - transaction",
  },
  { value: "annualWeightedAverage", label: "Weighted average - annual" },
];

export const transactionTypeOptions = [
  { value: "opening", label: "Brought forward" },
  { value: "acquisition", label: "Acquisition" },
  { value: "eebrTransfer", label: "EEBR transfer" },
  { value: "otherOutflow", label: "Other outflow" },
];

export const normalizeTrackerShares = (value, type) => {
  if (value === "" || value === null || value === undefined) return "";
  const quantity = Number(value);
  if (!Number.isFinite(quantity)) return value;
  return type === "eebrTransfer" || type === "otherOutflow"
    ? -Math.abs(quantity)
    : Math.abs(quantity);
};

export const transactionTypeLabels = Object.fromEntries(
  transactionTypeOptions.map(({ value, label }) => [value, label]),
);

export const arrangementOptions = [
  {
    value: "companyTreasury",
    label: "Company treasury shares",
    column: "acquired",
  },
  {
    value: "holdingTreasury",
    label: "Holding-company treasury shares",
    column: "acquired",
  },
  {
    value: "spvOpenMarket",
    label: "SPV open-market shares",
    column: "acquired",
  },
  {
    value: "newCompanyShares",
    label: "Newly issued company shares",
    column: "newlyIssued",
  },
  {
    value: "newHoldingShares",
    label: "Newly issued holding-company shares",
    column: "newlyIssued",
  },
];

export const formulaFieldsByArrangement =
  QUALIFYING_FIELDS_BY_ARRANGEMENT;

export const formulaAmountFields = QUALIFYING_COLUMN_FIELDS;

// Use the highest existing suffix so deleting a middle row cannot duplicate IDs.
const nextId = (prefix, rows, idField) => {
  const highest = rows.reduce((max, row) => {
    const id = String(row[idField] || "");
    const suffix = id.startsWith(`${prefix}-`)
      ? Number(id.slice(prefix.length + 1))
      : 0;
    return Number.isSafeInteger(suffix) ? Math.max(max, suffix) : max;
  }, 0);
  return `${prefix}-${String(highest + 1).padStart(3, "0")}`;
};

const createUniqueClaimLineId = (claimLines = []) => {
  const existingIds = new Set(
    claimLines.map((line) => line.claimLineId).filter(Boolean),
  );
  let claimLineId;
  do {
    claimLineId = uuidv4();
  } while (existingIds.has(claimLineId));
  return claimLineId;
};

const createUniqueLineId = (claimLines = []) => {
  const highestLineNumber = claimLines.reduce((highest, line) => {
    const match = /^L(\d+)$/.exec(String(line.lineId || ""));
    return match ? Math.max(highest, Number(match[1])) : highest;
  }, 0);
  return `L${String(highestLineNumber + 1).padStart(3, "0")}`;
};

export const createClaimLine = (data, initialValues = {}) => ({
  column: "acquired",
  arrangement: "companyTreasury",
  schemeOrPoolLabel: "",
  description: "",
  eebrType: "ESO",
  grantDate: "",
  exerciseOrVestingDate: "",
  sellingRestrictionEndDate: "",
  sharesTransferred: 0,
  amountI: 0,
  amountII: 0,
  amountIII: 0,
  amountIV: 0,
  employeePayment: 0,
  manualCostEntry: !data.useCostTracker,
  ...initialValues,
  rechargeOrLiabilityDate:
    (initialValues.arrangement || "companyTreasury") === "companyTreasury"
      ? ""
      : initialValues.rechargeOrLiabilityDate || "",
  lineId:
    initialValues.lineId &&
    !(data.claimLines || []).some(
      (line) => line.lineId === initialValues.lineId,
    )
      ? initialValues.lineId
      : createUniqueLineId(data.claimLines || []),
  claimLineId:
    initialValues.claimLineId &&
    !(data.claimLines || []).some(
      (line) => line.claimLineId === initialValues.claimLineId,
    )
      ? initialValues.claimLineId
      : createUniqueClaimLineId(data.claimLines || []),
});

export const createTrackerTransaction = (
  data,
  type = "acquisition",
  poolId,
) => ({
  transactionId: nextId("txn", data.transactions, "transactionId"),
  serialNo: nextId(
    "txn",
    data.transactions.map((row) => ({ serialNo: row.serialNo || row.transactionId })),
    "serialNo",
  ),
  poolId: poolId || data.costPools[0]?.poolId || "",
  date: String(data.basisPeriodStart || "").slice(0, 10),
  sequence:
    Math.max(0, ...data.transactions.map((row) => Number(row.sequence) || 0)) +
    1,
  type,
  shares: "",
  shareCost: "",
  directAcquisitionCharges: "",
  excludedFinanceCost: "",
  linkedClaimLineId: "",
  reference: "",
  outflowDescription: "",
});

// Renumber display references only; stable IDs and FIFO tie-break order stay intact.
export const renumberTrackerTransactions = (data, orderedIds) => {
  const validIds = new Set(data.transactions.map((row) => row.transactionId));
  const ids = [...new Set([
    ...orderedIds.filter((id) => validIds.has(id)),
    ...validIds,
  ])];
  const serials = new Map(
    ids.map((id, index) => [id, `txn-${String(index + 1).padStart(3, "0")}`]),
  );
  return {
    ...data,
    transactions: data.transactions.map((row) => ({
      ...row,
      serialNo: serials.get(row.transactionId),
    })),
  };
};

export const renumberClaimLines = (data) => ({
  ...data,
  claimLines: data.claimLines.map((line, index) => ({
    ...line,
    lineId: `L${String(index + 1).padStart(3, "0")}`,
  })),
});

export const removeTrackerTransaction = (
  data,
  transactionId,
  orderedIds = data.transactions.map((row) => row.transactionId),
) => {
  const remaining = {
    ...data,
    transactions: data.transactions.filter(
      (row) => row.transactionId !== transactionId,
    ),
  };
  return renumberTrackerTransactions(
    remaining,
    orderedIds.filter((id) => id !== transactionId),
  );
};

export const updateClaimLine = (data, claimLineId, field, value) => ({
  ...data,
  claimLines: data.claimLines.map((line) => {
    if (line.claimLineId !== claimLineId) return line;
    // IDs are immutable because they identify the row in the UI and in links.
    if (field === "claimLineId" || field === "lineId") return line;
    if (
      field === "rechargeOrLiabilityDate" &&
      line.arrangement === "companyTreasury"
    ) {
      return line;
    }
    if (field !== "arrangement") return { ...line, [field]: value };
    const applicableFields = formulaFieldsByArrangement[value] || [];
    return {
      ...line,
      arrangement: value,
      rechargeOrLiabilityDate:
        value === "companyTreasury" ? "" : line.rechargeOrLiabilityDate,
      column:
        arrangementOptions.find((option) => option.value === value)?.column ||
        line.column,
      ...Object.fromEntries(
        formulaAmountFields
          .filter((key) => !applicableFields.includes(key))
          .map((key) => [key, 0]),
      ),
    };
  }),
});

export const removeClaimLine = (data, claimLineId) => {
  const remaining = {
    ...data,
    claimLines: data.claimLines.filter((line) => line.claimLineId !== claimLineId),
    transactions: data.transactions.filter(
      (row) => row.linkedClaimLineId !== claimLineId,
    ),
  };
  return renumberTrackerTransactions(
    renumberClaimLines(remaining),
    remaining.transactions.map((row) => row.transactionId),
  );
};

export const createInitialSharedBasedPaymentData = ({
  scheduleId = "",
  yearOfAssessment = "",
  financialYearStart = "",
  financialYearEnd = "",
} = {}) => ({
  schemaVersion: SHARE_BASED_PAYMENT_DATA_SCHEMA_VERSION,
  scheduleId,
  ya: yearOfAssessment,
  basisPeriodStart: String(financialYearStart || "").slice(0, 10),
  basisPeriodEnd: String(financialYearEnd || "").slice(0, 10),
  useCostTracker: true,
  showFifoShares: false,
  costPools: [createInitialCostPool()],
  transactions: [],
  claimLines: [],
  sourceData: [],
});

export const isSharedBasedPaymentDataEmpty = (data) => {
  if (!data || typeof data !== "object" || Array.isArray(data)) return true;

  if (
    data.schemaVersion !== undefined ||
    typeof data.useCostTracker === "boolean"
  ) {
    return false;
  }

  return [
    data.costPools,
    data.transactions,
    data.claimLines,
    data.sourceData,
  ].every(
    (collection) => !Array.isArray(collection) || collection.length === 0,
  );
};

export const initializeSharedBasedPaymentData = (data, context = {}) => {
  const initialData = createInitialSharedBasedPaymentData(context);

  if (isSharedBasedPaymentDataEmpty(data)) {
    return {
      ...data,
      ...initialData,
    };
  }

  const usedClaimLineIds = new Set();
  const usedLineIds = new Set();
  const claimLines = Array.isArray(data.claimLines)
    ? data.claimLines.map((line) => {
        let claimLineId = line.claimLineId;
        if (!claimLineId || usedClaimLineIds.has(claimLineId)) {
          claimLineId = createUniqueClaimLineId(
            Array.from(usedClaimLineIds, (id) => ({ claimLineId: id })),
          );
        }
        usedClaimLineIds.add(claimLineId);

        let lineId = line.lineId;
        if (!lineId || usedLineIds.has(lineId)) {
          lineId = createUniqueLineId(
            Array.from(usedLineIds, (id) => ({ lineId: id })),
          );
        }
        usedLineIds.add(lineId);

        return createClaimLine(
          {
            ...initialData,
            ...data,
            claimLines: [],
          },
          { ...line, claimLineId, lineId },
        );
      })
    : [];

  const validClaimLineIds = new Set(claimLines.map((line) => line.claimLineId));

  const sourcePool =
    Array.isArray(data.costPools) && data.costPools.length
      ? data.costPools[0]
      : initialData.costPools[0];
  const costPool = createInitialCostPool(sourcePool);

  return {
    ...initialData,
    ...Object.fromEntries(
      Object.entries(data).filter(([, value]) => value !== undefined),
    ),
    // The Tax Comp company period is authoritative when an existing schedule
    // is reopened after its financial year end has changed.
    ...(context.yearOfAssessment ? { ya: initialData.ya } : {}),
    ...(context.financialYearStart
      ? { basisPeriodStart: initialData.basisPeriodStart }
      : {}),
    ...(context.financialYearEnd
      ? { basisPeriodEnd: initialData.basisPeriodEnd }
      : {}),
    // One schedule represents one share pool. Any additional pools are
    // intentionally ignored; users can create another schedule instead.
    costPools: [costPool],
    transactions: Array.isArray(data.transactions)
      ? data.transactions
          .filter(
            (row) =>
              !row.linkedClaimLineId ||
              validClaimLineIds.has(row.linkedClaimLineId),
          )
          .map((row) => ({
            ...createTrackerTransaction(
              {
                ...initialData,
                ...data,
                transactions: [],
              },
              row.type,
              costPool.poolId,
            ),
            ...row,
            transactionId: row.transactionId || uuidv4(),
            serialNo: row.serialNo || row.transactionId,
            poolId: costPool.poolId,
            shares: normalizeTrackerShares(row.shares, row.type),
          }))
      : [],
    claimLines,
    sourceData: Array.isArray(data.sourceData) ? data.sourceData : [],
  };
};
